import { Resource } from "../resource/resource.model";
import { BridgeMappingRule, FieldMapping, ScheduleType } from "./bridge-integration.model";

// Re-export ScheduleType for convenience
export { ScheduleType } from "./bridge-integration.model";

/**
 * 4Bridge Task Model
 * Represents a sync task configuration within an integration
 */
export class BridgeTask extends Resource {
    integrationId: string;
    /**
     * The connection this task reads from. An integration links two apps with no direction of its
     * own, so each task carries the pair it runs — that is what lets one task go App 1 → App 2 and
     * a sibling go the other way. Must be one of the integration's two connections; the API
     * validates that and rejects anything else.
     * Undefined on tasks saved before per-task direction: those run the integration's own order.
     */
    sourceConnectionId?: string;
    /** The connection this task writes to. See {@link sourceConnectionId}. */
    destConnectionId?: string;
    name: string;
    sourceObject: string;
    targetObject: string;
    action: TaskAction;
    matchField?: string;
    scheduleType: string;
    cronExpression?: string;
    fieldMappings?: FieldMapping[];
    isEnabled: boolean;
    status: string;
    lastRunAt?: Date;
    nextRunAt?: Date;
    startedAt?: Date;
    completedAt?: Date;

    // Task dependency
    dependsOnTaskId?: string;
    dependsOnTaskName?: string;

    // Delete from source list after processing
    deleteFromSourceList?: boolean;

    // Source-specific configuration (e.g., Marketo static list error handling)
    sourceConfig?: { [key: string]: string };

    // Marketo consent-sync fields (F4): wire-format keys matching the 4Bridge worker's
    // BridgeTask Mongo entity 1:1. Coexist with the camelCase fields above (sourceConfig,
    // deleteFromSourceList) pending consolidation of the API DTOs onto a single wire format.
    source_config?: Record<string, string>;
    dest_config?: Record<string, string>;
    transform_rules?: BridgeMappingRule[];
    delete_from_source_list?: boolean;

    // Execution stats
    recordsProcessed?: number;
    recordsSuccess?: number;
    recordsFailed?: number;
    errorMessage?: string;

    createdAt?: Date;
    updatedAt?: Date;
}

/**
 * Task action enum
 */
export enum TaskAction {
    CREATE = 'create',
    UPDATE = 'update',
    UPSERT = 'upsert',
    DELETE = 'delete',
    SYNC = 'sync',
    // Marketo consent-sync (F4): bulk update matched by email, bypassing per-record lookups.
    UPDATE_ALL_BY_EMAIL = 'updateAllByEmail'
}

/**
 * Task status enum
 */
export enum TaskStatus {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    RUNNING = 'running',
    ERROR = 'error'
}

/**
 * Task trigger type enum (for execution logs)
 */
export enum TaskTrigger {
    MANUAL = 'manual',
    SCHEDULED = 'scheduled',
    WEBHOOK = 'webhook',
    SYSTEM = 'system'
}

/**
 * Task summary for dashboard
 */
export interface TaskSummary {
    totalTasks: number;
    activeTasks: number;
    pendingTasks: number;
    runningTasks: number;
    completedTasks: number;
    failedTasks: number;
    errorTasks: number;
    recordsProcessed: number;
    recordsSuccess: number;
    recordsFailed: number;
}

/**
 * Task filter options
 */
export interface TaskFilterOptions {
    integrationId?: string;
    status?: string;
    startDate?: Date;
    endDate?: Date;
    page?: number;
    pageSize?: number;
}

/**
 * Create task request DTO
 */
export interface CreateTaskRequest {
    integrationId: string;
    /** Omit both to inherit the integration's order; see {@link BridgeTask.sourceConnectionId}. */
    destConnectionId?: string;
    sourceConnectionId?: string;
    name: string;
    sourceObject: string;
    targetObject: string;
    action: string;
    matchField?: string;
    scheduleType: string;
    cronExpression?: string;
    fieldMappings?: FieldMapping[];
    isEnabled?: boolean;
    sourceConfig?: { [key: string]: string };
    dependsOnTaskId?: string;
    deleteFromSourceList?: boolean;
}

/**
 * Update task request DTO
 */
export interface UpdateTaskRequest {
    /** Naming one side flips the task; the API derives the other. See {@link CreateTaskRequest}. */
    destConnectionId?: string;
    sourceConnectionId?: string;
    name?: string;
    sourceObject?: string;
    targetObject?: string;
    action?: string;
    matchField?: string;
    scheduleType?: string;
    cronExpression?: string;
    fieldMappings?: FieldMapping[];
    isEnabled?: boolean;
    sourceConfig?: { [key: string]: string };
    dependsOnTaskId?: string;
    deleteFromSourceList?: boolean;
}

/**
 * Available objects per connector type
 */
export const CONNECTOR_OBJECTS: { [key: string]: string[] } = {
    // 4TM Products
    '4comply': ['Permissions', 'ComplianceInputs', 'RightRequests'],
    '4preferences': ['ContactPreferences', 'ContactDetails'],
    '4segments': ['Segment', 'SegmentMember', 'Audience'],

    // Marketing Automation Platforms
    'eloqua': ['Contacts', 'CustomObjects', 'Accounts', 'EmailGroups'],
    'marketo': ['Lead', 'Company', 'Opportunity', 'CustomObjects'],
    'hubspot': ['HubSpotContacts', 'Companies', 'Deals', 'Lists', 'MarketingEmails'],

    // CRM Systems
    'salesforce': ['Lead', 'Contact', 'Account', 'Opportunity', 'Campaign', 'CampaignMember'],
    'dynamics': ['Contact', 'Account', 'Lead', 'Opportunity', 'MarketingList'],

    // Generic
    'rest_api': ['CustomEndpoint']
};
